import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtomValue, useSetAtom } from "jotai";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { usePackageMonthlyPrice } from "@/features/Packages/hooks/usePackageMonthlyPrice";
import { usePackageNeedsAttention } from "@/features/Packages/hooks/usePackageNeedsAttention";
import { ProfileStackNavigatorParamList } from "@/navigators/ProfileStackNavigator/types";
import { ChevronRightIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { usePackageDisplayName } from "@whoosh/features/src/Packages";
import { convertDateStringToObj } from "@whoosh/utilities/src/dates";

import {
  CurrentPackagesModalAtom,
  goToDetailPageAtom,
  goToListPageAtom,
} from "../../CurrentPackagesModal/atoms";
import { ModalListState } from "../../CurrentPackagesModal/types";
import { PackageItem_memberPackage$key } from "./__generated__/PackageItem_memberPackage.graphql";

type Props = {
  memberPackageRef: PackageItem_memberPackage$key | null | undefined;
  status: "ACTIVE" | "EXPIRED" | "UPCOMING";
};
export const PackageItem = ({ memberPackageRef, status }: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const currentState = useAtomValue(CurrentPackagesModalAtom);
  const goToDetailPage = useSetAtom(goToDetailPageAtom);
  const goToListPage = useSetAtom(goToListPageAtom);
  const navigation =
    useNavigation<NavigationProp<ProfileStackNavigatorParamList>>();

  const memberPackage = useFragment(
    PackageItemFragmentString,
    memberPackageRef
  );

  const packageName = usePackageDisplayName(memberPackage?.package);
  const startDate = memberPackage?.startsOn
    ? format(convertDateStringToObj(memberPackage.startsOn), "MMM do, yyyy")
    : "";
  const endDate = memberPackage?.endsOn
    ? format(convertDateStringToObj(memberPackage?.endsOn), "MMM do, yyyy")
    : "";
  const billingType = memberPackage?.package?.billingType;
  const monthlyPrice = usePackageMonthlyPrice(memberPackage);

  const isCancelled = !!memberPackage?.cancelledOn;

  const subTextString =
    status === "ACTIVE"
      ? billingType === "ONE_TIME" || isCancelled
        ? endDate
          ? t`Ends ${endDate}`
          : ""
        : monthlyPrice
        ? t`Monthly ${currencySymbol}${(monthlyPrice / 100).toFixed(2)}`
        : endDate
        ? t`Ends ${endDate}`
        : ""
      : status === "EXPIRED"
      ? endDate
        ? t`Expired on ${endDate}`
        : ""
      : startDate
      ? t`Upcoming on ${startDate}`
      : "";

  const onPress = () => {
    if (!memberPackage) return;

    if (Platform.OS === "web") {
      goToDetailPage({
        packageId: memberPackage.id,
        packageName: packageName || "",
        onBack: () => {
          goToListPage({ ...(currentState as ModalListState) });
        },
      });
    } else {
      if (memberPackage.package.__typename === "MembershipPackage") {
        navigation.navigate("Memberships", {
          screen: "MembershipDetail",
          params: { membershipId: memberPackage.id },
        });
      } else {
        navigation.navigate("Packages", {
          screen: "PackageDetail",
          params: { packageId: memberPackage.id },
        });
      }
    }
  };

  const packageNeedsAttention = usePackageNeedsAttention(memberPackage);

  return (
    <Pressable
      backgroundColor={
        packageNeedsAttention
          ? "surface-status-error-subtlest"
          : status === "EXPIRED"
          ? "surface-primary"
          : "surface-secondary"
      }
      flexDirection="row"
      alignItems="center"
      borderRadius="4"
      padding={4}
      gap={2}
      borderColor={status === "EXPIRED" ? "outline-default" : "transparent"}
      borderWidth={status === "EXPIRED" ? 1 : 0}
      onPress={onPress}
    >
      <VStack flexGrow={1} gap={1}>
        <Text
          variant="mainBaseSemiBold"
          color={status === "EXPIRED" ? "text-secondary" : "text-primary"}
        >
          {packageName}
        </Text>
        {subTextString ? (
          <Text
            variant="mainSmallMedium"
            color={status === "EXPIRED" ? "text-secondary" : "text-primary"}
          >
            {subTextString}
          </Text>
        ) : null}
      </VStack>
      <Box
        height={48}
        width={48}
        alignItems="center"
        justifyContent="center"
        borderRadius="2"
        backgroundColor="surface-primary"
      >
        <ChevronRightIcon
          color={status === "EXPIRED" ? "text-secondary" : "text-primary"}
        />
      </Box>
    </Pressable>
  );
};

const PackageItemFragmentString = graphql`
  fragment PackageItem_memberPackage on MemberPackage {
    id
    ...usePackageMonthlyPrice_memberPackage
    ...usePackageNeedsAttention_memberPackage
    package {
      __typename
      ...usePackageDisplayName_package
      billingType
      billingOptions {
        billingCharges {
          frequency
          squareVariation {
            amount
          }
        }
      }
    }
    cancelledOn
    startsOn
    endsOn
  }
`;
